import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaBolt, FaCalendarCheck, FaUsers, FaStar, FaBriefcase, FaClock } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import api from '../services/api';
import { getJobLocationLabel } from '../utils/jobLocationPresentation';
import { formatCompactJobPay } from '../utils/jobPayPresentation';

interface CityData {
  name: string;
  state: string;
  stateAbbr: string;
  slug: string;
  venues: string[];
  eventTypes: string[];
  topRoles: string[];
  metro?: string;
  searchLocation?: string;
}

const CITIES: Record<string, CityData> = {
  'las-vegas': {
    name: 'Las Vegas',
    state: 'Nevada',
    stateAbbr: 'NV',
    slug: 'las-vegas',
    venues: ['MGM Grand', 'Mandalay Bay Convention Center', 'T-Mobile Arena', 'Allegiant Stadium', 'Wynn Las Vegas', 'Las Vegas Convention Center'],
    eventTypes: ['Residency shows', 'Trade shows & conventions', 'Corporate events', 'Pool parties', 'Award shows', 'Fight nights'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Technician', 'Rigger', 'Video Tech', 'Stage Manager'],
  },
  'atlanta': {
    name: 'Atlanta',
    state: 'Georgia',
    stateAbbr: 'GA',
    slug: 'atlanta',
    venues: ['Mercedes-Benz Stadium', 'State Farm Arena', 'Georgia World Congress Center', 'Fox Theatre', 'Tabernacle', 'Cobb Energy Centre'],
    eventTypes: ['Film & TV production', 'Concerts & festivals', 'Corporate events', 'Trade shows', 'Theater', 'Sports events'],
    topRoles: ['Stagehand', 'Grip', 'Audio Engineer', 'Lighting Tech', 'Camera Operator', 'Production Assistant'],
  },
  'nashville': {
    name: 'Nashville',
    state: 'Tennessee',
    stateAbbr: 'TN',
    slug: 'nashville',
    venues: ['Ryman Auditorium', 'Bridgestone Arena', 'Grand Ole Opry', 'Ascend Amphitheater', 'Marathon Music Works', 'Music City Center'],
    eventTypes: ['Live music', 'Award shows (CMAs, Dove Awards)', 'Corporate events', 'Festivals', 'Theater', 'Church events'],
    topRoles: ['Audio Engineer', 'Stagehand', 'Lighting Tech', 'Backline Tech', 'Monitor Engineer', 'FOH Engineer'],
  },
  'los-angeles': {
    name: 'Los Angeles',
    state: 'California',
    stateAbbr: 'CA',
    slug: 'los-angeles',
    venues: ['Crypto.com Arena', 'Hollywood Bowl', 'SoFi Stadium', 'LA Convention Center', 'The Forum', 'Greek Theatre'],
    eventTypes: ['Film & TV production', 'Awards shows', 'Concerts', 'Corporate events', 'Music festivals', 'Brand activations'],
    topRoles: ['Stagehand', 'Grip', 'Audio Engineer', 'Lighting Tech', 'Video Engineer', 'Stage Manager'],
  },
  'orange-county': {
    name: 'Orange County',
    state: 'California',
    stateAbbr: 'CA',
    slug: 'orange-county',
    venues: ['Anaheim Convention Center', 'Honda Center', 'Angel Stadium', 'Segerstrom Center for the Arts', 'House of Blues Anaheim', 'Pacific Amphitheatre'],
    eventTypes: ['Conventions (NAMM, D23, VidCon)', 'Corporate events', 'Concerts', 'Theme park events', 'Sports events', 'Beach festivals'],
    topRoles: ['AV Technician', 'Stagehand', 'Audio Engineer', 'Lighting Tech', 'LED Tech', 'Rigger'],
  },
  'huntington-beach': {
    name: 'Huntington Beach',
    state: 'California',
    stateAbbr: 'CA',
    slug: 'huntington-beach',
    venues: ['Huntington Beach Pier', 'Huntington Beach Convention & Visitors Bureau', 'Hilton Waterfront Beach Resort', 'Hyatt Regency Huntington Beach', 'Pacific City Events', 'Bolsa Chica State Beach'],
    eventTypes: ['Beach concerts & festivals', 'US Open of Surfing', 'Corporate beach events', 'Hotel AV events', 'Outdoor brand activations', 'Fourth of July events'],
    topRoles: ['AV Technician', 'Audio Engineer', 'Stagehand', 'Lighting Tech', 'Stage Manager', 'Production Assistant'],
  },
  'inland-empire': {
    name: 'Inland Empire',
    state: 'California',
    stateAbbr: 'CA',
    slug: 'inland-empire',
    venues: ['Toyota Arena', 'Riverside Convention Center', 'Yaamava Resort & Casino', 'Fox Performing Arts Center', 'Acrisure Arena', 'Ontario Convention Center'],
    eventTypes: ['Arena concerts', 'Casino entertainment', 'Corporate events', 'Convention work', 'Festival load-ins', 'Touring stops'],
    topRoles: ['Stagehand', 'AV Technician', 'Audio Engineer', 'Lighting Tech', 'Rigger', 'Video Tech'],
    metro: 'inland-empire',
  },
  'new-york': {
    name: 'New York',
    state: 'New York',
    stateAbbr: 'NY',
    slug: 'new-york',
    venues: ['Madison Square Garden', 'Barclays Center', 'Radio City Music Hall', 'Javits Center', 'Lincoln Center', 'Brooklyn Steel'],
    eventTypes: ['Broadway & theater', 'Corporate events', 'Fashion weeks', 'Concerts', 'Trade shows', 'TV production'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'Spot Operator', 'Carpenter', 'Wardrobe'],
  },
  'chicago': {
    name: 'Chicago',
    state: 'Illinois',
    stateAbbr: 'IL',
    slug: 'chicago',
    venues: ['McCormick Place', 'United Center', 'Soldier Field', 'Chicago Theatre', 'Navy Pier', 'Wintrust Arena'],
    eventTypes: ['Trade shows & conventions', 'Concerts', 'Corporate events', 'Festivals (Lollapalooza)', 'Theater', 'Sports events'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'Rigger', 'Forklift Operator', 'Video Tech'],
  },
  'dallas': {
    name: 'Dallas',
    state: 'Texas',
    stateAbbr: 'TX',
    slug: 'dallas',
    venues: ['AT&T Stadium', 'American Airlines Center', 'Kay Bailey Hutchison Convention Center', 'The Bomb Factory', 'Dos Equis Pavilion', 'Globe Life Field'],
    eventTypes: ['Corporate events', 'Concerts', 'Sports events', 'Trade shows', 'Church events', 'Brand activations'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'LED Tech', 'Video Engineer', 'Rigger'],
  },
  'houston': {
    name: 'Houston',
    state: 'Texas',
    stateAbbr: 'TX',
    slug: 'houston',
    venues: ['NRG Stadium', 'Toyota Center', 'George R. Brown Convention Center', 'Smart Financial Centre', 'House of Blues Houston', 'White Oak Music Hall'],
    eventTypes: ['Corporate events', 'Conventions & trade shows', 'Concerts', 'Sports events', 'Church events', 'Brand activations'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'LED Tech', 'Video Engineer', 'Rigger'],
  },
  'miami': {
    name: 'Miami',
    state: 'Florida',
    stateAbbr: 'FL',
    slug: 'miami',
    venues: ['Hard Rock Stadium', 'Kaseya Center', 'Miami Beach Convention Center', 'Bayfront Park', 'The Fillmore', 'FPL Solar Amphitheater'],
    eventTypes: ['Music festivals (Ultra, Rolling Loud)', 'Art fairs (Art Basel)', 'Corporate events', 'Nightclub events', 'Cruise ship turnarounds', 'Fashion events'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'LED Tech', 'Video Engineer', 'DJ Tech'],
  },
  'san-francisco': {
    name: 'San Francisco',
    state: 'California',
    stateAbbr: 'CA',
    slug: 'san-francisco',
    venues: ['Chase Center', 'Moscone Center', 'Bill Graham Civic Auditorium', 'The Warfield', 'Oracle Park', 'Palace of Fine Arts'],
    eventTypes: ['Tech conferences (Dreamforce, GDC)', 'Corporate events', 'Concerts', 'Product launches', 'Festivals (Outside Lands)', 'Galas'],
    topRoles: ['Audio Engineer', 'Video Engineer', 'Lighting Tech', 'Stagehand', 'LED Tech', 'AV Tech'],
  },
  'denver': {
    name: 'Denver',
    state: 'Colorado',
    stateAbbr: 'CO',
    slug: 'denver',
    venues: ['Red Rocks Amphitheatre', 'Ball Arena', 'Colorado Convention Center', 'Empower Field at Mile High', 'Mission Ballroom', 'Fillmore Auditorium'],
    eventTypes: ['Concerts (Red Rocks season)', 'Corporate events', 'Conventions', 'Festivals', 'Sports events', 'Outdoor events'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'Rigger', 'Stage Manager', 'Backline Tech'],
  },
  'seattle': {
    name: 'Seattle',
    state: 'Washington',
    stateAbbr: 'WA',
    slug: 'seattle',
    venues: ['Climate Pledge Arena', 'Washington State Convention Center', 'T-Mobile Park', 'Lumen Field', 'Paramount Theatre', 'Showbox SoDo'],
    eventTypes: ['Concerts & festivals', 'Tech corporate events', 'Trade shows & conventions', 'Theater', 'Sports events', 'Outdoor festivals'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'Video Engineer', 'Rigger', 'Stage Manager'],
  },
  'portland': {
    name: 'Portland',
    state: 'Oregon',
    stateAbbr: 'OR',
    slug: 'portland',
    venues: ['Moda Center', 'Oregon Convention Center', 'Arlene Schnitzer Concert Hall', 'McMenamins Crystal Ballroom', 'Roseland Theater', 'Providence Park'],
    eventTypes: ['Concerts & live music', 'Corporate events', 'Conventions', 'Festivals', 'Theater', 'Sports events'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'Stage Manager', 'Backline Tech', 'AV Technician'],
  },
  'boston': {
    name: 'Boston',
    state: 'Massachusetts',
    stateAbbr: 'MA',
    slug: 'boston',
    venues: ['TD Garden', 'Boston Convention & Exhibition Center', 'Fenway Park', 'Wang Theatre', 'House of Blues Boston', 'Agganis Arena'],
    eventTypes: ['Corporate events', 'Conventions & trade shows', 'Concerts', 'University events', 'Theater', 'Sports events'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'Video Engineer', 'Rigger', 'AV Technician'],
  },
  'washington-dc': {
    name: 'Washington DC',
    state: 'District of Columbia',
    stateAbbr: 'DC',
    slug: 'washington-dc',
    venues: ['Capital One Arena', 'Walter E. Washington Convention Center', 'Kennedy Center', 'Nationals Park', 'Anthem', 'Warner Theatre'],
    eventTypes: ['Association conventions', 'Political & government events', 'Corporate events', 'Concerts', 'Galas & fundraisers', 'International summits'],
    topRoles: ['Audio Engineer', 'Video Engineer', 'Lighting Tech', 'Stagehand', 'Stage Manager', 'AV Technician'],
  },
  'phoenix': {
    name: 'Phoenix',
    state: 'Arizona',
    stateAbbr: 'AZ',
    slug: 'phoenix',
    venues: ['State Farm Stadium', 'Footprint Center', 'Phoenix Convention Center', 'Ak-Chin Pavilion', 'Talking Stick Resort Amphitheatre', 'Celebrity Theatre'],
    eventTypes: ['Corporate events', 'Concerts', 'Sports & mega-events', 'Trade shows', 'Church events', 'Outdoor festivals'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'LED Tech', 'Video Engineer', 'Rigger'],
  },
  'austin': {
    name: 'Austin',
    state: 'Texas',
    stateAbbr: 'TX',
    slug: 'austin',
    venues: ['Moody Center', 'Circuit of the Americas', 'Austin Convention Center', 'ACL Live at the Moody Theater', 'Stubb\'s Outdoor Amphitheatre', 'Emo\'s'],
    eventTypes: ['Music festivals (SXSW, ACL)', 'Corporate events', 'Formula 1', 'Tech conferences', 'Concerts', 'Outdoor events'],
    topRoles: ['Audio Engineer', 'Stagehand', 'Lighting Tech', 'Backline Tech', 'Stage Manager', 'Video Engineer'],
  },
  'new-orleans': {
    name: 'New Orleans',
    state: 'Louisiana',
    stateAbbr: 'LA',
    slug: 'new-orleans',
    venues: ['Caesars Superdome', 'Smoothie King Center', 'Ernest N. Morial Convention Center', 'Saenger Theatre', 'Orpheum Theater', 'Tipitina\'s'],
    eventTypes: ['Festivals (Jazz Fest, Essence, French Quarter Fest)', 'Conventions', 'Concerts', 'Mardi Gras events', 'Corporate events', 'Sports events'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'Backline Tech', 'Stage Manager', 'Rigger'],
  },
  'philadelphia': {
    name: 'Philadelphia',
    state: 'Pennsylvania',
    stateAbbr: 'PA',
    slug: 'philadelphia',
    venues: ['Wells Fargo Center', 'Lincoln Financial Field', 'Pennsylvania Convention Center', 'The Met Philadelphia', 'Franklin Music Hall', 'Academy of Music'],
    eventTypes: ['Concerts', 'Corporate events', 'Conventions', 'Sports events', 'Theater', 'Festivals'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'Rigger', 'Spot Operator', 'Video Tech'],
  },
  'san-diego': {
    name: 'San Diego',
    state: 'California',
    stateAbbr: 'CA',
    slug: 'san-diego',
    venues: ['Petco Park', 'Pechanga Arena San Diego', 'San Diego Convention Center', 'The Observatory North Park', 'Humphreys Concerts by the Bay', 'Balboa Theatre'],
    eventTypes: ['Comic-Con', 'Biotech conventions', 'Military events', 'Concerts', 'Corporate events', 'Outdoor festivals'],
    topRoles: ['AV Technician', 'Audio Engineer', 'Stagehand', 'Lighting Tech', 'Video Engineer', 'Stage Manager'],
  },
  'minneapolis': {
    name: 'Minneapolis',
    state: 'Minnesota',
    stateAbbr: 'MN',
    slug: 'minneapolis',
    venues: ['Target Center', 'U.S. Bank Stadium', 'Minneapolis Convention Center', 'First Avenue', 'State Theatre', 'Orpheum Theatre'],
    eventTypes: ['Corporate events', 'Concerts', 'Theater', 'Conventions', 'Sports events', 'Festivals (Soundset, Twin Cities Pride)'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'Rigger', 'Stage Manager', 'AV Technician'],
  },
  'tampa': {
    name: 'Tampa',
    state: 'Florida',
    stateAbbr: 'FL',
    slug: 'tampa',
    venues: ['Amalie Arena', 'Tropicana Field', 'Tampa Convention Center', 'MidFlorida Credit Union Amphitheatre', 'Yuengling Center', 'Straz Center for the Performing Arts'],
    eventTypes: ['Corporate events', 'Concerts', 'Sports events', 'Conventions', 'Theater', 'Outdoor festivals'],
    topRoles: ['Stagehand', 'Audio Engineer', 'Lighting Tech', 'LED Tech', 'Video Engineer', 'Rigger'],
  },
  'charlotte': {
    name: 'Charlotte',
    state: 'North Carolina',
    stateAbbr: 'NC',
    slug: 'charlotte',
    venues: ['Spectrum Center', 'Bank of America Stadium', 'Charlotte Convention Center', 'PNC Music Pavilion', 'Cabarrus Arena & Events Center', 'Bojangles Coliseum'],
    eventTypes: ['Corporate events (banking & finance)', 'NASCAR events', 'Concerts', 'Conventions', 'Sports events', 'Festivals'],
    topRoles: ['AV Technician', 'Stagehand', 'Audio Engineer', 'Lighting Tech', 'Video Engineer', 'Stage Manager'],
  },
  'orlando': {
    name: 'Orlando',
    state: 'Florida',
    stateAbbr: 'FL',
    slug: 'orlando',
    venues: ['Orange County Convention Center', 'Amway Center', 'Dr. Phillips Center for the Arts', 'Universal Studios Events', 'Camping World Stadium', 'Rosen Centre Hotel'],
    eventTypes: ['Trade shows & conventions', 'Theme park events', 'Corporate conferences', 'Concerts', 'Church events', 'Brand activations'],
    topRoles: ['AV Technician', 'Stagehand', 'LED Technician', 'Audio Engineer', 'Video Engineer', 'Rigger'],
  },
  'san-jose': {
    name: 'San Jose',
    state: 'California',
    stateAbbr: 'CA',
    slug: 'san-jose',
    venues: ['San Jose Convention Center', 'SAP Center', 'San Jose Civic', 'PayPal Park', 'City National Civic', 'Tech Interactive'],
    eventTypes: ['Tech product launches', 'Developer conferences', 'Corporate events', 'Concerts', 'Trade shows', 'Esports events'],
    topRoles: ['Video Engineer', 'LED Technician', 'Audio Engineer', 'AV Technician', 'Camera Operator', 'Stage Manager'],
  },
  'anaheim': {
    name: 'Anaheim',
    state: 'California',
    stateAbbr: 'CA',
    slug: 'anaheim',
    venues: ['Anaheim Convention Center', 'Honda Center', 'Angel Stadium', 'House of Blues Anaheim', 'City National Grove of Anaheim', 'Disneyland Resort Events'],
    eventTypes: ['Conventions (NAMM, D23, VidCon, WonderCon)', 'Concerts', 'Sports events', 'Corporate events', 'Theme park events', 'Trade shows'],
    topRoles: ['Stagehand', 'AV Technician', 'Rigger', 'Audio Engineer', 'LED Technician', 'Forklift Operator'],
  },
};

const CITY_SLUGS = Object.keys(CITIES);

interface LiveJob {
  id: number;
  title: string;
  company: string;
  location: string | null;
  source?: string | null;
  rate: string | null;
  rate_min: number | null;
  rate_max: number | null;
  rate_type: string | null;
  job_type: string | null;
  scraped_at: string;
}

function fmtRate(job: LiveJob): string {
  return formatCompactJobPay(job);
}
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const CityGigsPage: React.FC = () => {
  const { city } = useParams<{ city: string }>();
  const [liveJobs, setLiveJobs] = useState<LiveJob[]>([]);
  const [jobCount, setJobCount] = useState<number | null>(null);

  const data = city ? CITIES[city] : null;
  const dataName = data?.name;
  const dataMetro = data?.metro;
  const dataSearchLocation = data?.searchLocation;
  const jobsLocationQuery = dataMetro
    ? `metro=${encodeURIComponent(dataMetro)}`
    : `location=${encodeURIComponent(dataSearchLocation || dataName || '')}`;

  useEffect(() => {
    if (!dataName) return;
    let isActive = true;
    api.get(`/scraped-jobs?${jobsLocationQuery}&limit=6`)
      .then((res: any) => {
        if (!isActive) return;
        const jobs = res.data?.jobs ?? res.data ?? [];
        setLiveJobs(jobs);
        setJobCount(res.data?.total ?? jobs.length);
      })
      .catch(() => {});
    return () => {
      isActive = false;
    };
  }, [dataName, jobsLocationQuery]);

  if (!city || !CITIES[city]) {
    return <Navigate to="/jobs" replace />;
  }

  const pageTitle = `${data.name} Stagehand & AV Jobs | StageCrew`;
  const pageUrl = `https://stagecrew.app/gigs/${data.slug}`;

  return (
    <div>
      <SEOHead
        title={pageTitle}
        description={`Find stagehand, AV technician, and live event crew jobs in ${data.name}, ${data.stateAbbr}. Browse sourced listings and save your work preferences.`}
        url={pageUrl}
        keywords={`stagehand jobs ${data.name}, AV technician jobs ${data.name}, ${data.name} stagehand work, live event crew ${data.name}, ${data.name} AV gigs, freelance stagehand ${data.stateAbbr}, ${data.name} event staffing`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: pageTitle,
          url: pageUrl,
          description: `Browse sourced stagehand and AV technician gigs in ${data.name}, ${data.stateAbbr}. Listing volume and requirements change as sources update.`,
          mainEntity: {
            '@type': 'Service',
            name: `StageCrew ${data.name}`,
            serviceType: 'Stagehand & AV Job Matching',
            areaServed: {
              '@type': 'City',
              name: data.name,
              containedInPlace: {
                '@type': 'State',
                name: data.state,
              },
            },
            provider: {
              '@type': 'Organization',
              name: 'StageCrew',
              url: 'https://stagecrew.app',
            },
          },
        }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-800 via-indigo-700 to-purple-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <FaMapMarkerAlt className="text-yellow-400 text-xl" />
            <span className="text-yellow-300 font-semibold text-lg">{data.name}, {data.stateAbbr}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            Stagehand & AV Jobs in {data.name}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 mb-4 max-w-3xl">
            Explore sourced crew listings in {data.name}.
          </p>
          <p className="text-lg text-blue-200 mb-8 max-w-3xl">
            Browse sourced listings in {data.name}. Job volume, dates, rates, and requirements change as the original sources update.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/register?role=worker"
              className="bg-white text-indigo-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg text-center"
            >
              Find Gigs in {data.name}
            </Link>
            <Link
              to="/for-employers"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:bg-opacity-10 transition text-center"
            >
              Hire Crew in {data.name}
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-gray-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-yellow-400">{jobCount ?? '—'}</p>
              <p className="text-gray-400 text-sm mt-1">Sourced Listings</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-green-400">{data.venues.length}</p>
              <p className="text-gray-400 text-sm mt-1">Major Venues</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-400">{data.topRoles.length}</p>
              <p className="text-gray-400 text-sm mt-1">Crew Roles</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-purple-400">Source</p>
              <p className="text-gray-400 text-sm mt-1">Application Details</p>
            </div>
          </div>
        </div>
      </section>

      {/* Example venues */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Example Venues in {data.name}</h2>
          <p className="text-gray-600 mb-8">Examples of established venues in this market.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.venues.map((venue) => (
              <div key={venue} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <FaStar className="text-yellow-500 flex-shrink-0" />
                <span className="font-medium text-gray-800">{venue}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* In-Demand Roles + Event Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Crew Roles to Search</h2>
              <div className="space-y-3">
                {data.topRoles.map((role) => (
                  <div key={role} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                    <FaUsers className="text-indigo-500" />
                    <span className="font-medium text-gray-800">{role}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Event Types</h2>
              <div className="space-y-3">
                {data.eventTypes.map((type) => (
                  <div key={type} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                    <FaCalendarCheck className="text-teal-500" />
                    <span className="font-medium text-gray-800">{type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Insight */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-8 md:p-12 border border-indigo-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              <FaBolt className="inline text-yellow-500 mr-2" />
              Review Each {data.name} Listing
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Listing volume changes over time. Check the original source, work date, location, rate, and application method before you act.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">
            How to Get {data.name} Gigs on StageCrew
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Create Your Profile', desc: `Add the skills, rate, and ${data.name} availability you want employers to review.` },
              { step: '2', title: 'Review Matches', desc: `StageCrew compares current listings with your saved profile and work preferences.` },
              { step: '3', title: 'Review & Apply', desc: 'Review the source and application details, then apply on your terms.' },
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                <div className="bg-indigo-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent sourced jobs */}
      {liveJobs.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Recent Job Listings in {data.name}
                </h2>
                {jobCount !== null && (
                  <p className="text-gray-500 mt-1">{jobCount} sourced listings currently shown</p>
                )}
              </div>
              <Link to={`/jobs?${jobsLocationQuery}`}
                className="text-indigo-600 font-semibold hover:text-indigo-800 transition text-sm">
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveJobs.map((job) => (
                <Link key={job.id} to={`/jobs/${job.id}`}
                  className="block p-5 bg-gray-50 border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition">
                  <div className="flex flex-col items-start gap-2 mb-2 sm:flex-row sm:justify-between">
                    <h3 className="min-w-0 font-semibold text-gray-900 text-sm leading-snug line-clamp-2">{job.title}</h3>
                    <span className="max-w-full text-left text-green-600 font-bold text-sm sm:text-right">{fmtRate(job)}</span>
                  </div>
                  {job.company && <p className="text-gray-600 text-xs mb-1">{job.company}</p>}
                  <p className="flex items-center gap-1 text-xs text-gray-500">
                    <FaMapMarkerAlt className="flex-shrink-0" aria-hidden="true" />
                    {getJobLocationLabel(job)}
                  </p>
                  <div className="flex items-center justify-between mt-3">
                    {job.job_type && (
                      <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <FaBriefcase className="text-xs" />{job.job_type}
                      </span>
                    )}
                    <span className="text-xs text-gray-400 flex items-center gap-1 ml-auto">
                      <FaClock className="text-xs" />Imported {timeAgo(job.scraped_at)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link to="/register?role=worker"
                className="inline-block bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition">
                Create a Profile for {data.name} Matches
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Work in {data.name}?
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Browse current {data.name} listings and create a free profile for saved preferences and profile-based matches.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register?role=worker"
              className="bg-white text-indigo-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg"
            >
              Sign Up Free
            </Link>
            <Link
              to="/for-employers"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:bg-opacity-10 transition"
            >
              I&apos;m Hiring in {data.name}
            </Link>
          </div>
        </div>
      </section>

      {/* Vendor CTA */}
      <section className="py-12 bg-indigo-50 border-t border-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-gray-900 text-lg mb-1">Are you a vendor or labor provider in {data.name}?</h3>
            <p className="text-gray-500 text-sm">Request a StageCrew vendor-directory listing so event producers can review your service details.</p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <Link to="/vendors" className="text-indigo-600 font-medium text-sm hover:underline">Browse vendors →</Link>
            <a href={`mailto:edwin@stagecrew.app?subject=Vendor Listing - ${data.name}`} className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition">
              Get Listed
            </a>
          </div>
        </div>
      </section>

      {/* Other Cities */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Explore Other Markets</h2>
          <div className="flex flex-wrap gap-3">
            {CITY_SLUGS.filter((s) => s !== city).map((slug) => (
              <Link
                key={slug}
                to={`/gigs/${slug}`}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition font-medium text-sm"
              >
                <FaMapMarkerAlt className="inline mr-1 text-xs" />
                {CITIES[slug].name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export { CITIES, CITY_SLUGS };
export default CityGigsPage;
